import type { MmFlagView, SideView } from "@screener/protocol";
import { Badge } from "@screener/uikit/components/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { sideLabel } from "@/lib/labels";

/**
 * Бейджи стороны стакана и флагов ММ §9/§4.5 — часть экрана, не UIKit:
 * им нужны сторона стакана и признак ММ, а UIKit ни о том, ни о другом
 * не знает. Строка таблицы и заголовок детальной панели несут одно и то же,
 * и здесь это одно место, а не два скопированных набора классов.
 */

interface SideBadgeProps {
  side: SideView;
  /** Есть хотя бы один флаг ММ (§4.5): сторона называется жёлтым, а не своим цветом. */
  flagged: boolean;
}

export function SideBadge({ side, flagged }: SideBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "h-[17px] border-transparent px-1.5 text-[11px] font-medium tracking-wider",
        flagged
          ? "bg-mm-wash text-mm"
          : side === "bid"
            ? "bg-bid-wash text-bid"
            : "bg-ask-wash text-ask",
      )}
    >
      {sideLabel(side)}
    </Badge>
  );
}

interface MmFlagBadgesProps {
  /**
   * Коды флагов — типом контракта, а не `string[]`: переименование кода
   * в `proto` обязано ломать сборку web, а не рисоваться как есть
   * (`apps/web/CLAUDE.md`; сверка `spec-auditor` при приёмке этапа 5).
   */
  flags: MmFlagView[];
}

/** Значки флагов ММ (§4.5) — пометка, не фильтрация; скрытие делает фильтр таблицы. */
export function MmFlagBadges({ flags }: MmFlagBadgesProps) {
  return (
    <>
      {flags.map((flag) => (
        <Tooltip key={flag}>
          <TooltipTrigger
            render={
              <Badge
                variant="outline"
                className="h-[17px] border-transparent bg-mm-wash px-1.5 text-[11px] text-mm"
              />
            }
          >
            {flag}
          </TooltipTrigger>
          <TooltipContent>Признак маркетмейкера (§4.5). Пометка, не фильтрация.</TooltipContent>
        </Tooltip>
      ))}
    </>
  );
}

/** Подсказка значка «ММ» строки карточки: коды флагов через запятую (§123). */
export function mmMarkHint(flags: MmFlagView[]): string {
  return `Признак маркетмейкера: ${flags.join(", ")}`;
}

interface MmMarkProps {
  flags: MmFlagView[];
}

/**
 * Значок «ММ» строки карточки (§123) — один на плотность, а не по значку на
 * флаг: строка в одну линию высотой 22 px. Перечень кодов — в подсказке и в
 * доступном имени строки. Вызывается только при непустых флагах.
 */
export function MmMark({ flags }: MmMarkProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            data-slot="mm-mark"
            variant="outline"
            size="count"
            className="border-transparent bg-mm-wash text-mm"
          />
        }
      >
        ММ
      </TooltipTrigger>
      <TooltipContent>{mmMarkHint(flags)}</TooltipContent>
    </Tooltip>
  );
}
